import { Home, Ban, RotateCcw } from "lucide-react";

const STATUS_C = { open: "#30D158", isolated: "#FF3B30", flooded: "#38bdf8" };

export function ShelterPanel({ data, onSelect }) {
  const s = data?.summary;
  const rows = (data?.shelters || []).filter((r) => r.role === "public_shelter").slice(0, 60);
  return (
    <div data-testid="shelter-panel" className="flex flex-col h-full min-h-0">
      <div className="p-3 border-b border-border grid grid-cols-3 gap-1.5">
        <Kpi testid="shelter-usable-capacity" label="Usable beds" value={s?.usable_capacity?.toLocaleString() ?? "—"} sub={`${s?.open ?? 0}/${s?.public_shelters ?? 0} open`} />
        <Kpi testid="shelter-occupancy-total" label="Checked in" value={s?.occupancy?.toLocaleString() ?? "—"} />
        <Kpi testid="shelter-demand-total" label="Predicted" value={s?.predicted_demand?.toLocaleString() ?? "—"}
          sub={s?.over_capacity ? `${s.over_capacity} over cap` : "within cap"} warn={!!s?.over_capacity} />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-1.5">
        {rows.map((r) => {
          const occ = r.occupancy_pct ?? 0;
          const dem = Math.min(100, r.demand_pct ?? 0);
          return (
            <button key={r.asset_id} data-testid={`shelter-row-${r.asset_id}`} onClick={() => onSelect(r.asset_id)}
              className="w-full text-left bg-slate-900/60 border border-border rounded-md px-2.5 py-1.5 hover:border-cyan/50 transition-colors">
              <div className="flex items-center justify-between gap-2">
                <span className="font-heading font-semibold text-xs text-slate-100 truncate flex items-center gap-1.5">
                  <Home className="w-3 h-3 shrink-0" style={{ color: STATUS_C[r.status] }} /> {r.name}
                </span>
                <span className="font-mono-t text-[9px] text-slate-400 shrink-0">{r.occupancy}/{r.capacity}</span>
              </div>
              <div className="relative h-1.5 rounded bg-slate-800 mt-1 overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-amber-400/40" style={{ width: `${dem}%` }} />
                <div className="absolute inset-y-0 left-0 bg-cyan" style={{ width: `${Math.min(100, occ)}%` }} />
              </div>
              <div className="font-mono-t text-[9px] text-slate-500 mt-0.5 flex justify-between">
                <span>{r.parish} · {r.status}</span>
                <span style={{ color: (r.demand_pct ?? 0) > 100 ? "#FF3B30" : undefined }}>demand {r.predicted_demand} ({r.demand_pct ?? 0}%)</span>
              </div>
            </button>
          );
        })}
      </div>
      <div data-testid="shelter-method" title={s?.method} className="px-3 py-1.5 border-t border-border font-mono-t text-[8.5px] text-slate-500 truncate">{s?.method}</div>
    </div>
  );
}

export function ClosuresPanel({ closures, session, onReopen, onSelect }) {
  return (
    <div data-testid="closures-panel" className="flex flex-col h-full min-h-0">
      <div className="p-3 border-b border-border font-mono-t text-[10px] text-slate-400">
        <span data-testid="closures-count" className="text-risk-critical font-bold">{closures.length}</span> active field closures · click a road, bridge or port on the map to report
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-1.5">
        {!closures.length && <div className="font-mono-t text-[11px] text-slate-500 text-center py-6">No roads reported impassable</div>}
        {closures.map((c) => (
          <div key={c.id} data-testid={`closure-row-${c.id}`} className="bg-slate-900/60 border border-risk-critical/40 rounded-md px-2.5 py-1.5">
            <div className="flex items-center justify-between gap-2">
              <button onClick={() => onSelect(c.asset_id)} className="font-heading font-semibold text-xs text-slate-100 truncate flex items-center gap-1.5 hover:text-cyan">
                <Ban className="w-3 h-3 text-risk-critical shrink-0" /> {c.name}
              </button>
              {session && (
                <button data-testid={`closure-reopen-${c.id}`} onClick={() => onReopen(c.id)} title="Reopen"
                  className="text-emerald-400 hover:text-emerald-300 shrink-0"><RotateCcw className="w-3.5 h-3.5" /></button>
              )}
            </div>
            <div className="font-mono-t text-[9px] text-slate-500 mt-0.5">
              {c.asset_type} · {c.reason} · {c.parish || ""} · {c.reported_by} · {new Date(c.created_at).toLocaleString()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, warn, testid }) {
  return (
    <div data-testid={testid} className="bg-slate-900/60 border border-border rounded-md px-2 py-1.5">
      <div className="font-mono-t text-[8px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-heading font-bold text-base leading-tight text-slate-100">{value}</div>
      {sub && <div className="font-mono-t text-[9px]" style={{ color: warn ? "#FF3B30" : "#64748b" }}>{sub}</div>}
    </div>
  );
}
